import React, { useContext, useEffect, useState } from 'react';
import classes from './styles.module.scss';
import {
  IonCard,
  IonCol,
  IonGrid,
  IonIcon,
  IonRippleEffect,
  IonRow,
} from '@ionic/react';
import { DeviceContext } from '../../../deviceType';
import { Link, useLocation } from 'react-router-dom';
import WishlistIcon from '../wish-list/wish-list-icon';
import ImageCarousel from '../../../components/ImageCarousel';
import { ProductType } from '../../../types/product';
import { caretDown, caretUp, shareSocialSharp } from 'ionicons/icons';
import classNames from 'classnames';
import BackButton from '../../../components/backButton';
import { IoCartOutline, IoClose } from 'react-icons/io5';
import AddToCartModal from '../cart/inc/addToCartModal';
import useGlobalContext from '../../../hooks/useStore';
import ErrorToast from '../../../components/error_toast';
import { share } from '../../../utils/common';

const ProductCard = (props: {
  data: ProductType;
  disableWishlist: boolean;
  isVendor?: boolean;
}) => {
  const size = useContext(DeviceContext);
  const location = useLocation();
  const [item, setItem] = useState<ProductType>({} as ProductType);
  const [locations, setLocations] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [storeOpen, setStoreOpen] = useState<boolean>(true);
  const { cartState, tagState } = useGlobalContext();
  const [isInCart, setIsInCart] = useState<boolean>(false);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    type?: 'error' | 'warning' | 'success';
    text: string;
  }>();

  useEffect(() => {
    setItem(props.data);
  }, [props.data]);

  useEffect(() => {
    let state = cartState.cart!.find((e) => e.product.id === props.data.id);
    if (state) {
      setIsInCart(true);
    }
  }, [cartState.cart]);

  useEffect(() => {
    if (item?.store) {
      const locs = item?.store.location!.split(',');
      const locations = locs?.slice(0, 2).join(',');
      setLocations(locations!);
    }
    if (item?.price) {
      const itemPrice = item.price
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      setPrice(itemPrice);
    }
  }, [item]);

  useEffect(() => {
    if (message?.text) {
      setOpenToast(true);
    }
  }, [message]);

  const shareAction = async () => {
    let url = new URL(window.location.href);
    const result = await share({
      text: `Check out ${item?.name} on Bazaarcheap!`,
      url: url.toString(),
    });
    if (result?.error) {
      setMessage({ text: result.error, type: 'error' });
    }
  };

  const statusBadge = classNames(
    'px-3 py-[2px] text-xs rounded-md capitalize font-semibold',
    {
      'bg-orange-200 text-orange-500': props.data.status === 'pending',
      'bg-gray-200 text-gray-500': props.data.status === 'draft',
      'bg-red-200 text-red-500': props.data.status === 'rejected',
      'bg-green-200 text-green-500': props.data.status === 'published',
    }
  );

  return (
    <>
      <ErrorToast
        isOpen={openToast}
        message={message?.text!}
        onClose={() => {
          setOpenToast(false);
        }}
        type={message?.type!}
        position="bottom"
      />
      <IonCard
        className={
          classes.productCard +
          ' w-full h-auto m-0 relative overflow-hidden border-none rounded-none bg-transparent'
        }
      >
        <AddToCartModal
          trigger={`single-product-${props.data.id}-trigger`}
          product={props.data}
        />

        {size.width > 768 && (
          <IonGrid className={classes.desktopGrid + ' size-full p-0 m-0'}>
            <IonRow className={'w-full h-[75dvh] '}>
              <IonCol size="5" className={'size-full p-0 m-0'}>
                <IonRow className={'size-full '}>
                  <IonCol size={'12'} className="size-full p-0 relative flex">
                    <ImageCarousel
                      data={props.data.images}
                      name={props.data.name}
                    />

                    {!props.isVendor && (
                      <div
                        className={
                          'cursor-pointer absolute !aspect-square w-[2.5rem] h-auto top-5 right-5 bg-background rounded-full flex items-center justify-center p-[2%] z-10'
                        }
                        onClick={shareAction}
                      >
                        <IonIcon
                          icon={shareSocialSharp}
                          color="primary"
                          size="default"
                        ></IonIcon>
                      </div>
                    )}

                    <div
                      className={
                        'cursor-pointer absolute !aspect-square w-[2.5rem] overflow-hidden h-auto top-5 left-5 bg-white rounded-full flex items-center justify-center z-10'
                      }
                    >
                      <BackButton />
                    </div>

                    {props.isVendor && props.data.outOfStock && (
                      <div className="absolute  items-center top-5 right-5 z-50 bg-background rounded-md text-primary font-semibold text-sm  px-3 py-2">
                        Out of Stock
                      </div>
                    )}
                    <div
                      className={
                        'absolute size-auto bottom-5 right-5 flex items-center'
                      }
                    >
                      {!props.isVendor && (
                        <div
                          className={
                            ' ml-2 cursor-pointer relative !aspect-square w-[3rem] overflow-hidden h-auto bg-background rounded-full flex items-center justify-center z-10 ion-activatable ripple-parent circles'
                          }
                          id={`single-product-${props.data.id}-trigger`}
                        >
                          <IonRippleEffect />
                          <span className="size-full flex items-center justify-center p-[0.8rem]">
                            <IoCartOutline
                              size="100%"
                              className={classNames({
                                'text-secondary': isInCart,
                                'text-primary': !isInCart,
                              })}
                            />
                          </span>
                        </div>
                      )}
                      {!props.disableWishlist && (
                        <div
                          className={
                            'ml-2 cursor-pointer relative !aspect-square w-[3rem] overflow-hidden h-auto bg-background rounded-full flex items-center justify-center z-10 ion-activatable ripple-parent circles'
                          }
                        >
                          <IonRippleEffect />
                          <WishlistIcon productId={props.data.id} />
                        </div>
                      )}
                    </div>
                  </IonCol>
                </IonRow>
              </IonCol>
              <IonCol
                size="7"
                className={'size-full py-4 m-0 overflow-y-scroll '}
              >
                {' '}
                <IonRow
                  className={
                    classes.info +
                    ' w-full h-auto pt-4 px-4 max-h-[14vh] overflow-hidden items-center'
                  }
                >
                  <IonCol size={'12'} className="h-full">
                    <div className={classes.title}>{item?.name}</div>
                  </IonCol>
                </IonRow>
                {item.tags && (
                  <div className="w-full h-auto flex items-start gap-1 flex-wrap px-3 py-2">
                    {item.tags?.map((tag: any) => {
                      return (
                        <div
                          key={tag}
                          className="w-auto h-auto flex justify-center !text-xs items-center !m-0 px-2 py-1 bg-gray-300 text-primary hover:bg-gray-200 rounded-md cursor-pointer"
                        >
                          {tag}
                        </div>
                      );
                    })}
                  </div>
                )}
                <IonRow className={classes.price + ' w-full h-fit py-2 px-4'}>
                  <IonCol size={'12'}>UGX {price}</IonCol>
                </IonRow>
                {props.isVendor && (
                  <div className="flex w-full px-4">
                    <span className={statusBadge}>{props.data.status}</span>
                  </div>
                )}
                <IonRow
                  className={
                    classes.productDetails + ' w-full h-auto pb-2 px-4'
                  }
                >
                  <IonCol>
                    <IonRow
                      className={
                        classes.head +
                        ' w-full h-auto border-b-[1px] border-gray-400 cursor-pointer py-2 mb-2'
                      }
                      onClick={() => {
                        setDetailsOpen(!detailsOpen);
                      }}
                    >
                      <IonCol size="10" className={classes.label}>
                        Product/Service Details
                      </IonCol>
                      <IonCol
                        size="2"
                        className="w-auto h-fit flex items-center justify-center"
                      >
                        {detailsOpen && (
                          <IonIcon
                            icon={caretUp}
                            mode="ios"
                            color="primary"
                          ></IonIcon>
                        )}
                        {!detailsOpen && (
                          <IonIcon
                            icon={caretDown}
                            mode="ios"
                            color="primary"
                          ></IonIcon>
                        )}
                      </IonCol>
                    </IonRow>
                    {detailsOpen && (
                      <div
                        className={classes.content}
                        dangerouslySetInnerHTML={{ __html: item?.details! }}
                      ></div>
                    )}
                  </IonCol>
                </IonRow>
                <IonRow
                  className={classes.storeDetails + ' w-full h-auto pb-2 px-4'}
                >
                  <IonCol>
                    <IonRow
                      className={
                        classes.head +
                        ' w-full h-auto border-b-[1px] border-gray-400 cursor-pointer py-2 mb-2'
                      }
                      onClick={() => {
                        setStoreOpen(!storeOpen);
                      }}
                    >
                      <IonCol size="10" className={classes.label}>
                        Business Details
                      </IonCol>
                      <IonCol
                        size="2"
                        className="w-auto h-fit flex items-center justify-center"
                      >
                        {storeOpen && (
                          <IonIcon
                            icon={caretUp}
                            mode="ios"
                            color="primary"
                          ></IonIcon>
                        )}
                        {!storeOpen && (
                          <IonIcon
                            icon={caretDown}
                            mode="ios"
                            color="primary"
                          ></IonIcon>
                        )}
                      </IonCol>
                    </IonRow>
                    {storeOpen && (
                      <div
                        className={
                          classes.content + ' w-full h-auto flex px-4 flex-col'
                        }
                      >
                        <Link
                          className="w-full h-auto my-0 py-2"
                          to={{
                            pathname: `/${item?.store?.slug}`,
                            state: { id: item?.store?.id },
                          }}
                        >
                          {item?.store?.name}
                        </Link>
                        <div
                          className={
                            classes.locations + ' w-full h-auto flex flex-col '
                          }
                        >
                          <div className={classes.label + ' w-full h-auto'}>
                            Locations
                          </div>
                          <div
                            className={
                              classes.list +
                              ' w-full h-auto mb-1 pl-2 flex flex-col items-start'
                            }
                          >
                            {item.store?.location!.split(',').map((s, idx) => {
                              return (
                                <div
                                  key={idx}
                                  className={
                                    classes.item + ' w-full h-auto py-2'
                                  }
                                >
                                  {s}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div
                          className={
                            classes.contacts + ' w-full h-auto flex flex-col '
                          }
                        >
                          <div className={classes.label + ' w-full h-auto'}>
                            Contacts
                          </div>
                          <div
                            className={
                              classes.list +
                              ' w-full h-auto mb-1 pl-2 flex flex-col items-start'
                            }
                          >
                            {item.store
                              ?.phoneNumber!.split(',')
                              .map((s, idx) => {
                                return (
                                  <div
                                    key={idx}
                                    className={
                                      classes.item + ' w-full h-auto py-2'
                                    }
                                  >
                                    {s}
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    )}
                  </IonCol>
                </IonRow>
              </IonCol>
            </IonRow>
          </IonGrid>
        )}

        {size.width <= 768 && (
          <IonGrid className={classes.grid + ' w-full h-auto p-0 '}>
            <IonRow className={'w-full h-auto aspect-[2/2.5] '}>
              <IonCol size={'12'} className="size-auto p-0 relative">
                <ImageCarousel
                  data={props.data.images}
                  name={props.data.name}
                />
                {!props.isVendor && (
                  <div
                    className={
                      'cursor-pointer absolute !aspect-square w-[2.5rem] h-auto top-5 right-5 bg-white rounded-full flex items-center justify-center p-[2%] z-10'
                    }
                    onClick={shareAction}
                  >
                    <IonIcon
                      icon={shareSocialSharp}
                      color="primary"
                      size="default"
                    ></IonIcon>
                  </div>
                )}
                <div
                  className={
                    'cursor-pointer absolute !aspect-square w-[2.5rem] overflow-hidden h-auto top-5 left-5 bg-white rounded-full flex items-center justify-center z-10'
                  }
                >
                  <BackButton />
                </div>
                {props.isVendor && props.data.outOfStock && (
                  <div className="absolute  items-center top-5 right-5 z-50 bg-background rounded-md text-primary font-semibold text-sm  px-3 py-2">
                    Out of Stock
                  </div>
                )}
                <div
                  className={
                    'absolute size-auto bottom-5 right-5 flex items-center'
                  }
                >
                  {!props.isVendor && (
                    <div
                      className={
                        ' ml-4 cursor-pointer relative !aspect-square w-[3rem] overflow-hidden h-auto bg-white rounded-full flex items-center justify-center z-10 ion-activatable ripple-parent circles'
                      }
                      id={`single-product-${props.data.id}-trigger`}
                    >
                      <IonRippleEffect />
                      <span className="size-full flex items-center justify-center p-[0.8rem]">
                        <IoCartOutline
                          size="100%"
                          className={classNames({
                            'text-secondary': isInCart,
                            'text-primary': !isInCart,
                          })}
                        />
                      </span>
                    </div>
                  )}
                  {!props.disableWishlist && (
                    <div
                      className={
                        'ml-4 cursor-pointer relative !aspect-square w-[3rem] overflow-hidden h-auto bg-white rounded-full flex items-center justify-center z-10 ion-activatable ripple-parent circles'
                      }
                    >
                      <IonRippleEffect />
                      <WishlistIcon productId={props.data.id} />
                    </div>
                  )}
                </div>
              </IonCol>
            </IonRow>
            <IonRow
              className={
                classes.info +
                ' w-full h-auto pt-4 px-4 max-h-[14vh] overflow-hidden'
              }
            >
              <IonCol size={'12'} className="h-full">
                <div className={classes.title}>{item?.name}</div>
              </IonCol>
            </IonRow>

            {item.tags && (
              <div className="w-full h-auto flex items-start gap-1 flex-wrap px-3 py-2">
                {item?.tags?.map((tag: any) => {
                  return (
                    <div
                      key={tag}
                      className="w-auto h-auto flex justify-center !text-xs items-center !m-0 px-2 py-1 bg-gray-300 text-primary hover:bg-gray-200 rounded-md cursor-pointer"
                    >
                      {tag}
                    </div>
                  );
                })}
              </div>
            )}

            <IonRow className="text-secondary w-full h-auto pb-2 px-4 text-lg font-medium">
              <IonCol size={'12'}>UGX {price}</IonCol>
            </IonRow>

            {props.isVendor && (
              <div className="flex w-full px-4">
                <span className={statusBadge}>{props.data.status}</span>
              </div>
            )}

            <IonRow
              className={classes.productDetails + ' w-full h-auto pb-2 px-4'}
            >
              <IonCol>
                <IonRow
                  className={
                    classes.head +
                    ' w-full h-auto border-b-[1px] border-gray-400 cursor-pointer py-2 mb-2'
                  }
                  onClick={() => {
                    setDetailsOpen(!detailsOpen);
                  }}
                >
                  <IonCol size="10" className={classes.label}>
                    Product/Service Details
                  </IonCol>
                  <IonCol
                    size="2"
                    className="w-auto h-fit flex items-center justify-center"
                  >
                    {detailsOpen && (
                      <IonIcon
                        icon={caretUp}
                        mode="ios"
                        color="primary"
                      ></IonIcon>
                    )}
                    {!detailsOpen && (
                      <IonIcon
                        icon={caretDown}
                        mode="ios"
                        color="primary"
                      ></IonIcon>
                    )}
                  </IonCol>
                </IonRow>
                {detailsOpen && (
                  <div
                    className={classes.content}
                    dangerouslySetInnerHTML={{ __html: item?.details! }}
                  ></div>
                )}
              </IonCol>
            </IonRow>

            <IonRow
              className={classes.storeDetails + ' w-full h-auto pb-2 px-4'}
            >
              <IonCol>
                <IonRow
                  className={
                    classes.head +
                    ' w-full h-auto border-b-[1px] border-gray-400 cursor-pointer py-2 mb-2'
                  }
                  onClick={() => {
                    setStoreOpen(!storeOpen);
                  }}
                >
                  <IonCol size="10" className={classes.label}>
                    Business Details
                  </IonCol>
                  <IonCol
                    size="2"
                    className="w-auto h-fit flex items-center justify-center"
                  >
                    {storeOpen && (
                      <IonIcon
                        icon={caretUp}
                        mode="ios"
                        color="primary"
                      ></IonIcon>
                    )}
                    {!storeOpen && (
                      <IonIcon
                        icon={caretDown}
                        mode="ios"
                        color="primary"
                      ></IonIcon>
                    )}
                  </IonCol>
                </IonRow>
                {storeOpen && (
                  <div
                    className={
                      classes.content + ' w-full h-auto flex px-4 flex-col'
                    }
                  >
                    <Link
                      className="w-full h-auto my-0 py-2"
                      to={{
                        pathname: `/${item?.store?.slug}`,
                        state: { id: item?.store?.id },
                      }}
                    >
                      {item?.store?.name}
                    </Link>
                    <div
                      className={
                        classes.locations + ' w-full h-auto flex flex-col '
                      }
                    >
                      <div className={classes.label + ' w-full h-auto'}>
                        Locations
                      </div>
                      <div
                        className={
                          classes.list +
                          ' w-full h-auto mb-1 pl-2 flex flex-col items-start'
                        }
                      >
                        {item.store?.location!.split(',').map((s, idx) => {
                          return (
                            <div
                              key={idx}
                              className={classes.item + ' w-full h-auto py-2'}
                            >
                              {s}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div
                      className={
                        classes.contacts + ' w-full h-auto flex flex-col '
                      }
                    >
                      <div className={classes.label + ' w-full h-auto'}>
                        Contacts
                      </div>
                      <div
                        className={
                          classes.list +
                          ' w-full h-auto mb-1 pl-2 flex flex-col items-start'
                        }
                      >
                        {item.store?.phoneNumber!.split(',').map((s, idx) => {
                          return (
                            <div
                              key={idx}
                              className={classes.item + ' w-full h-auto py-2'}
                            >
                              {s}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </IonCol>
            </IonRow>
          </IonGrid>
        )}
      </IonCard>
    </>
  );
};

export default ProductCard;
